import { useEffect, useRef, useState } from 'react';
import { basicSetup } from 'codemirror';
import { EditorState, Transaction } from '@codemirror/state';
import { EditorView, keymap } from '@codemirror/view';
import { html } from '@codemirror/lang-html';
import { indentWithTab } from '@codemirror/commands';
import { formatHtmlCode } from './formatHtmlCode';
import styles from './HtmlCodeEditor.module.css';

const theme = EditorView.theme({
    '&': { color: '#172033', backgroundColor: '#f8fafc', fontSize: '13px' },
    '.cm-scroller': { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace', lineHeight: '1.6', overflow: 'auto' },
    '.cm-content': { minHeight: '300px', padding: '12px 0', caretColor: '#172033' },
    '.cm-gutters': { backgroundColor: '#eef2f6', color: '#64748b', border: 'none' },
    '.cm-activeLine, .cm-activeLineGutter': { backgroundColor: '#e8eef7' },
    '.cm-cursor': { borderLeftColor: '#172033' },
    '&.cm-focused .cm-selectionBackground, .cm-selectionBackground': { backgroundColor: '#cbdff9' },
    '&.cm-focused': { outline: '2px solid #4f8bd6', outlineOffset: '-2px' },
}, { dark: false });

export default function HtmlCodeEditor({ value, onChange, id, describedBy }) {
    const containerRef = useRef(null);
    const viewRef = useRef(null);
    const initialValueRef = useRef(value);
    const onChangeRef = useRef(onChange);
    const syncingRef = useRef(false);
    const [formatting, setFormatting] = useState(false);
    const [error, setError] = useState(null);
    onChangeRef.current = onChange;

    useEffect(() => {
        const view = new EditorView({
            parent: containerRef.current,
            state: EditorState.create({
                doc: initialValueRef.current,
                extensions: [
                    basicSetup, html(), theme, EditorView.lineWrapping,
                    keymap.of([indentWithTab]),
                    EditorView.domEventHandlers({
                        keydown: event => {
                            // Editing code must not trigger the builder's block shortcuts.
                            event.stopPropagation();
                            return false;
                        },
                    }),
                    EditorView.contentAttributes.of({
                        id, 'aria-label': 'Codice HTML e CSS', 'aria-describedby': describedBy,
                        spellcheck: 'false', autocapitalize: 'off', autocorrect: 'off',
                    }),
                    EditorView.updateListener.of(update => {
                        if (update.docChanged && !syncingRef.current) {
                            setError(null);
                            onChangeRef.current(update.state.doc.toString());
                        }
                    }),
                ],
            }),
        });
        viewRef.current = view;
        return () => {
            viewRef.current = null;
            view.destroy();
        };
    }, [id, describedBy]);

    useEffect(() => {
        const view = viewRef.current;
        if (!view || view.state.doc.toString() === value) return;
        syncingRef.current = true;
        try {
            view.dispatch({
                changes: { from: 0, to: view.state.doc.length, insert: value },
                annotations: Transaction.addToHistory.of(false),
            });
        } finally {
            syncingRef.current = false;
        }
    }, [value]);

    const format = async () => {
        const view = viewRef.current;
        if (!view) return;
        const source = view.state.doc.toString();
        setFormatting(true);
        setError(null);
        try {
            const formatted = await formatHtmlCode(source);
            if (viewRef.current !== view) return;
            if (view.state.doc.toString() !== source) {
                setError('Il codice è cambiato durante la formattazione. Premi di nuovo Formatta.');
                return;
            }
            if (formatted !== source) {
                view.dispatch({ changes: { from: 0, to: view.state.doc.length, insert: formatted } });
            }
            view.focus();
        } catch (err) {
            if (viewRef.current === view) setError(`Impossibile formattare: ${err.message}`);
        } finally {
            if (viewRef.current === view) setFormatting(false);
        }
    };

    return <div className={styles.editor}>
        <div className={styles.toolbar}>
            <span>HTML · CSS · JS</span>
            <button type="button" onClick={format} disabled={formatting || !value.trim()}>
                {formatting ? 'Formattazione…' : 'Formatta'}
            </button>
        </div>
        <div className={styles.code} ref={containerRef} />
        <p className={styles.help}>Tab per indentare · Shift+Tab per ridurre il rientro · Esc, poi Tab per uscire</p>
        {error && <p className={styles.error} role="alert">{error}</p>}
    </div>;
}
